import { useEffect, useRef } from 'react';
import type { CheckLite } from '../worker/protocol';
import { useStore } from '../state/store';
import { mockupStatusKind } from '../state/mockup';
import { describeBackdrop, jobPrints, printSizeLine, skippedPrints, type JobPrint } from '../lib/mockup-ui';
import { mockupViewShown, printAreaLine } from '../lib/mockup-view';
import { inspectorSummary, rowKind } from '../lib/inspect-summary';
import { sepViewShown } from './useSepView';
import { Section } from './Section';
import { INSPECTOR_SECTION_ID } from '../lib/panel';

/** The section id the drawer's folded state is kept under — imported rather than
 *  written twice, because `persist.ts` has to know it to leave that state out of what
 *  is saved (see `INSPECTOR_SECTION_ID`). */
const INSPECTOR_ID = INSPECTOR_SECTION_ID;

export function InspectorPanel() {
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  // The drawer's own state, read here as well as in `Section`: a document whose
  // checks come back with a failure opens it (below), and opening something
  // that is already open would shut it.
  const collapsed = useStore((s) => s.viewer.collapsed[INSPECTOR_ID] ?? true);
  const toggleSection = useStore((s) => s.toggleSection);
  const overlays = useStore((s) => s.viewer.overlays);
  const toggleOverlay = useStore((s) => s.toggleOverlay);
  const view = useStore((s) => s.viewer.view);
  const setViewer = useStore((s) => s.setViewer);
  const appMode = useStore((s) => s.appMode);
  const templates = useStore((s) => s.templates);
  const templateStatus = useStore((s) => s.templateStatus);
  // The scene list, for the Backdrop row's name. Safe to subscribe to in every mode: it changes
  // only when a scene is imported or dropped, unlike `s.docs`.
  const scenes = useStore((s) => s.scenes);
  const glPreview = useStore((s) => s.glPreview);
  const checks = doc?.checks ?? [];
  // The separation's own checks (unmatched inks, coverage, moiré, mesh,
  // resolution) sit above the DTF ones in screen-print mode and count towards
  // the same header total: they are the ones that decide whether the films are
  // printable, so a fail there must not be hidden behind an "all clear".
  const sep = appMode === 'sep' ? doc?.sep : undefined;
  const sepChecks = sep?.checks ?? [];
  // Exactly what the viewer is painting — not merely "screen-print mode". In
  // sep mode with a failed or not-yet-built separation the canvas is still the
  // DTF preview, so its overlays still work and their rows must stay live.
  const sepOnCanvas = sepViewShown(appMode, doc?.sep);
  // The mockup's own rows. Gated exactly as the separation rows are: on what the
  // viewer is painting, not merely on the mode.
  const mockupShown = mockupViewShown(appMode, doc);
  const mockup = mockupShown ? doc?.mockup : undefined;
  const mockupTemplate = mockup ? templates.find((t) => t.template.id === mockup.job.templateId)?.template : undefined;
  /** Every print this job draws, the primary one first — one row each below. */
  const prints = mockup && mockupTemplate ? jobPrints(mockup.job, mockupTemplate) : [];
  // The art SIZES the extra prints are measured against, as a string — never the documents
  // themselves. This panel is mounted in every mode, and subscribing to `s.docs` would re-render
  // it on every unrelated per-document write; this value only changes when a size a row reports
  // does. A print on this document's own art reads `doc.run` directly.
  const extraArtKey = useStore((s) => prints.map((p) => {
    const d = p.docId && p.docId !== doc?.id ? s.docs.find((x) => x.id === p.docId) : undefined;
    return d?.run ? `${d.run.width}x${d.run.height}` : '';
  }).join(';'));
  const extraArts = extraArtKey.split(';');
  const artFor = (p: JobPrint, i: number): { width: number; height: number } | undefined => {
    // `docId: null` — and this document's own id, which `extraArtsFor` reads the same way — is
    // "this file", whose art is the run on screen.
    if (!p.docId || p.docId === doc?.id) return doc?.run ? { width: doc.run.width, height: doc.run.height } : undefined;
    const [w, h] = (extraArts[i] ?? '').split('x').map(Number);
    return w > 0 && h > 0 ? { width: w, height: h } : undefined;
  };
  const all = [...sepChecks, ...checks];
  // The pipeline's own warnings ("Upscaling by 2.3×…"): the one thing the panel
  // says about this document that nothing else says, so it must not be foldable
  // — it is rendered above the summary row rather than inside the drawer. Every
  // other line that used to sit in here (a failed plate build, a failed
  // screening push, a mockup that could not render, "Run the pipeline first")
  // lives in the pinned footer, which is always on screen and is their one home.
  const warnings = doc?.run?.warnings ?? [];
  // ...but the dot still answers for them. `errors` is what the FOOTER is
  // painting in the fail colour right now, read from the same values it reads
  // (`mockupStatusKind`, and `sep.error` as `SepStatus` renders it) rather than
  // derived a second time here, so a green dot can never sit above a red status
  // line. `sep.filmsError` is not counted: the footer shows it as a warning,
  // because the plates on screen are still the last build's.
  const errors = (sep?.error ? 1 : 0) + (appMode === 'mockup' && mockupStatusKind(doc, templateStatus) === 'error' ? 1 : 0);
  // The whole list in one line — what the panel shows with the drawer shut.
  const summary = inspectorSummary(all, errors);
  const busy = !!doc?.inspecting;
  // What the canvas is painting, which is what decides whether an overlay row
  // can do anything (see `rowKind`). Every separation check is plain text
  // whatever this says — its evidence is the plate itself, which the plate bar
  // already shows — because none of them carries `hasOverlay`.
  const painting = sepOnCanvas ? 'separation' : mockupShown ? 'mockup' : 'output';

  // A document whose checks come back with a failure opens the drawer itself,
  // ONCE: a first-time user must not have to know that "1 problem" hides the
  // sentence explaining it. Remembered per document id (in a ref, so it
  // survives every re-render but no reload) rather than in the store, so
  // re-opening it can never fight a shop that has just shut it.
  const autoOpened = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id = doc?.id;
    if (!id || summary.tone !== 'fail' || autoOpened.current.has(id)) return;
    autoOpened.current.add(id);
    if (collapsed) toggleSection(INSPECTOR_ID, false);
  }, [doc?.id, summary.tone, collapsed, toggleSection]);

  // The drawer is the LAST thing in a scrolling panel, so opening it while the
  // panel is scrolled to the bottom would add 800 px below the fold and change
  // nothing on screen but the triangle. Opening it brings its own summary line
  // to the top of the panel, where the rows it just revealed are the rest of
  // the column. Only on the open — never on the first render, and never when
  // the shop folds it away again.
  const summaryRef = useRef<HTMLSpanElement>(null);
  const wasCollapsed = useRef(collapsed);
  useEffect(() => {
    const opened = wasCollapsed.current && !collapsed;
    wasCollapsed.current = collapsed;
    if (opened) summaryRef.current?.closest('.section')?.scrollIntoView({ block: 'start' });
  }, [collapsed]);

  function row(c: CheckLite) {
    const on = !!overlays[c.id];
    // Audit #23: the rows that do something look like it, and only those. The
    // eye is on the button rows alone — a row that says "overlay" and paints
    // nothing when clicked is the thing that teaches a user to stop clicking.
    const kind = rowKind(c, painting);
    const body = (
      <>
        <span className={`pill ${c.status}`}>{c.status}</span>
        <span className="check-label">{c.label}</span>
        <span className="muted small">{c.unit ? `${c.value} ${c.unit}` : c.value}</span>
        {kind === 'button' && (
          <span className="check-eye small" style={{ color: on ? 'var(--accent)' : 'var(--muted)' }}>
            {/* The eye itself carries no words: the text beside it is the
                accessible name, and `aria-pressed` is the state. */}
            <span aria-hidden="true">{on ? '◉' : '○'}</span>
            {on ? 'overlay on' : 'overlay'}
          </span>
        )}
        {/* a <span>, not a <div>: a button may only contain phrasing content.
            As a grid item it is blockified anyway, so the layout is unchanged. */}
        <span className="check-msg small muted">{c.message}</span>
      </>
    );
    return kind === 'button' ? (
      <button key={c.id} type="button" className={`check check-btn${on ? ' on' : ''}`} aria-pressed={on}
        onClick={() => { if (view !== 'output') setViewer({ view: 'output' }); toggleOverlay(c.id); }}
        title="Click to toggle the overlay">
        {body}
      </button>
    ) : (
      <div key={c.id} className="check">{body}</div>
    );
  }

  return (
    <>
      {/* Above the drawer, not inside it: folding the checks away must never
          fold away the pipeline's own warning about what it had to do. */}
      {warnings.length > 0 && (
        <div className="panel-notices">
          {warnings.map((w, i) => <div key={i} className="small" style={{ color: 'var(--warn)' }}>{w}</div>)}
        </div>
      )}
      <Section
        /* "Details", not "Inspector": the heading IS the button that opens the
           drawer, and what the shop needs it to say is what clicking it gives
           them. The verdict itself is the summary line beside it. */
        id={INSPECTOR_ID} title="Details" defaultCollapsed
        /* Shut by default, unlike every other section (audit #1): the checks were
           574–860 px of the panel in every mode, and this line is the whole of
           what most jobs need from them. */
        summary={
          <span className="section-summary" ref={summaryRef}>
            <span className={`dot ${busy ? 'busy' : summary.tone}`} aria-hidden="true" />
            {busy ? 'checking…' : summary.text}
          </span>
        }
      >
        {!doc && <div className="muted small">Open a file to see its checks.</div>}
        {sep && (
          <>
            <h3 style={{ margin: '10px 0 8px' }}>Separation</h3>
            {/* `sep.error` and `sep.filmsError` are the pinned footer's, never
                here: a failed build must not be foldable. */}
            {!sep.error && !sepChecks.length && <div className="muted small">{sep.building ? 'Building the plates…' : 'No plates built yet.'}</div>}
            {sepChecks.map(row)}
            {/* Without a second heading the DTF checks below would read as part of
                the separation's own list. */}
            <h3 style={{ margin: '10px 0 8px' }}>Artwork</h3>
          </>
        )}
        {mockup && (
          <>
            <h3 style={{ margin: '10px 0 8px' }}>Mockup</h3>
            {/* Informational rows: nothing here toggles anything on the canvas, so
                they are inert `.check` divs rather than buttons. */}
            <div className="check"><span className="check-label">Template</span><span className="muted small">{mockupTemplate?.name ?? mockup.job.templateId}</span></div>
            {mockupTemplate && <div className="check"><span className="check-label">Print area</span><span className="muted small">{printAreaLine(mockupTemplate)}</span></div>}
            {/* One row per print — the job's own, then each extra in the order they composite — so a
                blank with a sleeve or a pocket reads as the several prints it actually is. A print
                whose document is closed (or has not been run) says so rather than a size. */}
            {prints.map((p, i) => (
              <div key={p.areaId || 'primary'} className="check">
                <span className="check-label">Print</span>
                <span className="muted small">
                  {p.name ? `${p.name}: ` : ''}{printSizeLine(p.placement, p.rotationDeg, artFor(p, i))}{p.enabled ? '' : ' · off'}
                </span>
              </div>
            ))}
            {/* The compositor reports ONE clipped flag for the whole render, not one per print, so
                with several prints up this can only say that something ran out of its area. Per-print
                flags are a change to `renderMockup`'s result, not to this panel. */}
            <div className="check"><span className="check-label">Clipped</span><span className="muted small">{mockup.clipped ? (prints.length > 1 ? 'yes (some print)' : 'yes') : 'no'}</span></div>
            {/* Prints the last render could not put anywhere, each with WHY: a document that has been
                closed (`no design`) reads differently from an area this blank does not have (`no such
                area on this blank` — a print retained from another blank, which the panel's "Retained
                prints" list is where to remove). The two fixes are different, so the two are said
                apart rather than both as a bare area name. */}
            {mockup.skipped.length > 0 && (
              <div className="check">
                <span className="check-label">Skipped</span>
                <span className="muted small">{skippedPrints(mockup.skipped, mockupTemplate).map((s) => `${s.name} — ${s.reason}`).join(', ')}</span>
              </div>
            )}
            {/* What the garment is photographed AGAINST — the studio backdrop the photo came with
                (`none`), a colour, a gradient, or one of the shop's own scenes, named. The same line
                the job ticket prints, so the panel, the inspector and the ticket cannot disagree —
                including `applied`, which reads `none` on a blank whose mask does not separate (the
                job keeps its backdrop there, but the render ignores it). */}
            <div className="check">
              <span className="check-label">Backdrop</span>
              <span className="muted small">
                {describeBackdrop(mockup.job.backdrop ?? null, scenes, {
                  applied: templateStatus[mockup.job.templateId]?.prepared?.separable !== false,
                })}
              </span>
            </div>
            {/* WHICH RENDERER THE PREVIEW USES — the sticky, app-wide answer, not a per-frame readout:
                once the GPU has drawn a frame this keeps saying so through the windows where the
                current frame happens to come from the CPU (a template whose maps have not landed yet,
                a document with no output). That is deliberate — see the store's `glPreview` — because
                the alternative is this row and the §8 banner flickering on every template switch.
                Either way the GPU path draws the interactive preview ONLY: every export, sheet and
                report is the worker's CPU composite whatever this says. */}
            <div className="check"><span className="check-label">Renderer</span><span className="muted small">{glPreview === 'gl' ? 'GPU (WebGL2)' : 'CPU (worker)'}</span></div>
            <h3 style={{ margin: '10px 0 8px' }}>Artwork</h3>
          </>
        )}
        {checks.map(row)}
      </Section>
    </>
  );
}
